import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminApi } from './useAdminApi';

const TypesTab = () => {
  const { adminApi } = useAdminApi();

  const [typesConfig, setTypesConfig] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [showAddType, setShowAddType] = useState(false);
  const [typeForm, setTypeForm] = useState({ key: '', label: '', description: '', use_cases: '' });

  useEffect(() => { fetchTypesConfig(); }, []);

  const fetchTypesConfig = async () => {
    setTypesLoading(true);
    try {
      const res = await adminApi('get', '/admin/types');
      setTypesConfig(res.data);
    } finally {
      setTypesLoading(false);
    }
  };

  const handleToggleType = async (key, currentEnabled) => {
    await adminApi('put', `/admin/types/${key}`, { enabled: !currentEnabled });
    fetchTypesConfig();
  };

  const handleSaveType = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await adminApi('put', `/admin/types/${editingType.key}`, {
          label: typeForm.label,
          description: typeForm.description,
          use_cases: typeForm.use_cases,
        });
        toast.success('Type updated');
      } else {
        await adminApi('post', '/admin/types', typeForm);
        toast.success('Type created');
      }
      setEditingType(null);
      setShowAddType(false);
      setTypeForm({ key: '', label: '', description: '', use_cases: '' });
      fetchTypesConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save type');
    }
  };

  const closeModal = () => {
    setShowAddType(false);
    setEditingType(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-xl text-charcoal">Manage Types</h2>
          <p className="text-charcoal/50 text-sm mt-1">Create, edit, and toggle conversation types.</p>
        </div>
        <button
          onClick={() => { setShowAddType(true); setEditingType(null); setTypeForm({ key: '', label: '', description: '', use_cases: '' }); }}
          className="flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm"
          style={{ background: '#ff780a' }}
          data-testid="add-type-button"
        >
          <Plus size={14} /> New Type
        </button>
      </div>

      {typesLoading ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin mx-auto" />
        </div>
      ) : (
        <div className="space-y-3">
          {typesConfig.map((tc) => (
            <div
              key={tc.key}
              data-testid={`type-config-${tc.key}`}
              className={`p-4 rounded-xl border flex items-start gap-4 ${tc.enabled ? 'bg-white/50 border-charcoal/5' : 'bg-charcoal/5 border-charcoal/10 opacity-60'}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-charcoal font-medium">{tc.label}</span>
                  <span className="text-charcoal/40 text-xs font-mono">{tc.key}</span>
                  {!tc.enabled && <span className="px-2 py-0.5 rounded text-xs bg-charcoal/10 text-charcoal/50">Hidden</span>}
                </div>
                <p className="text-charcoal/60 text-sm mt-1">{tc.description}</p>
                {tc.use_cases && <p className="text-charcoal/40 text-xs mt-0.5">{tc.use_cases}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => {
                    setEditingType(tc);
                    setShowAddType(true);
                    setTypeForm({ key: tc.key, label: tc.label, description: tc.description || '', use_cases: tc.use_cases || '' });
                  }}
                  className="p-2 rounded-lg hover:bg-charcoal/5 transition-colors"
                >
                  <Edit2 size={15} className="text-charcoal/50" />
                </button>
                <button
                  onClick={() => handleToggleType(tc.key, tc.enabled)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${tc.enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-charcoal/10 text-charcoal/50 hover:bg-charcoal/20'}`}
                >
                  {tc.enabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Type Modal */}
      <AnimatePresence>
        {showAddType && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(44, 36, 23, 0.5)' }}
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-lg rounded-2xl shadow-2xl p-6"
              style={{ backgroundColor: '#FFF8F0' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-xl text-charcoal">{editingType ? 'Edit Type' : 'New Type'}</h3>
                <button onClick={closeModal} className="p-2 rounded-full hover:bg-charcoal/5">
                  <X size={20} className="text-charcoal/60" />
                </button>
              </div>
              <form onSubmit={handleSaveType}>
                {!editingType && (
                  <div className="mb-4">
                    <label className="block text-sm text-charcoal/60 mb-2">Key (lowercase, no spaces)</label>
                    <input
                      value={typeForm.key}
                      onChange={(e) => setTypeForm(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') }))}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50"
                      placeholder="e.g. family"
                    />
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-sm text-charcoal/60 mb-2">Label</label>
                  <input
                    value={typeForm.label}
                    onChange={(e) => setTypeForm(prev => ({ ...prev, label: e.target.value }))}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50"
                    placeholder="e.g. Family"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm text-charcoal/60 mb-2">Description</label>
                  <input
                    value={typeForm.description}
                    onChange={(e) => setTypeForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50"
                    placeholder="e.g. Family relationships and gatherings"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm text-charcoal/60 mb-2">Use Cases</label>
                  <input
                    value={typeForm.use_cases}
                    onChange={(e) => setTypeForm(prev => ({ ...prev, use_cases: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50"
                    placeholder="e.g. Family dinners, holidays, reunions"
                  />
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={closeModal} className="flex-1 py-3 rounded-full border border-charcoal/20 text-charcoal hover:bg-charcoal/5">
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 py-3 rounded-full text-white" style={{ background: '#ff780a' }}>
                    {editingType ? 'Update' : 'Create'} Type
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TypesTab;
